import type { CheckInFactorName, CheckInFactors } from '@/lib/kioskSession';

import { kioskFetch } from './kioskFetch';
import { refusedFieldsIn } from './refusedFields';

/**
 * The only egress in the app: the four-factor identity lookup [KID-2].
 *
 * ⚠️ **These types are hand-written, and the convention (§ 6) says they should be generated** from the
 * api's OpenAPI schema via `npm run api:types`. That script reads `../iss-kiosk-api/openapi.json`, which the
 * backend does not emit yet — Swagger is served at `/docs` but never written to disk. Until it is, a
 * backend breaking change surfaces here as a runtime 400 in front of a candidate rather than as a typecheck
 * failure, which is exactly what generation exists to prevent. Tracked as follow-up; the types below mirror
 * `LookupResponseModel` and `ThrottledResponseModel` field for field.
 */

/** What the backend can diagnose. `assistance` is the neutral hand-off and carries no reason, by design. */
export const DIAGNOSIS = {
  new: 'new',
  returning: 'returning',
  activePlacement: 'active-placement',
  resume: 'resume',
  assistance: 'assistance',
  differentEmail: 'different-email',
} as const;

export type Diagnosis = (typeof DIAGNOSIS)[keyof typeof DIAGNOSIS];

/**
 * The candidate's required-credential state, as the lookup answers it [KID-2a; decisions 0440, 0233]:
 * `true` every required credential reads complete · `false` at least one is outstanding · `null` the read did
 * not happen, failed, or came back uninterpretable.
 */
export type RequiredCredentialsState = boolean | null;

/**
 * Whether the candidate's record holds a pay card, as the lookup answers it [KPC-1f AC-1; decision 0470]:
 * `true` a non-empty `Proxy ID #` · `false` none · `null` a value the backend could not interpret. A boolean
 * and nothing else — ⛔ no digit of the proxy ID ever travels on the lookup answer.
 */
export type PayCardOnFileState = boolean | null;

export interface LookupResult {
  readonly diagnosis: Diagnosis;
  readonly candidateId?: number;
  readonly emailOnFile?: string;
  /**
   * ⭐ **It travels, and NOTHING reads it** [KAP-5c AC-4; decisions 0233, 0440].
   *
   * The backend reads the credential state ONCE, at identification, and this is where its answer rides for
   * the rest of the session — on the diagnosis, not on anything built later. ⛔ **No screen, route,
   * selector or derived value may consume it**: `10a` draws one screen for every candidate [0128], and a
   * consumer would re-introduce the paragraph ISS deleted [0040]. It is kept, unread, so that paragraph can
   * return without touching KID-2's contract. ⛔ **Never re-read, polled or retried** [0029] — a `null`
   * stays `null` for the whole session. `tests/kap5cFlagHasNoReader.test.ts` fails a reader anywhere in `src/`.
   */
  readonly requiredCredentialsComplete: RequiredCredentialsState;
  /**
   * ⭐ **Read once, at identification, and it decides the application tail** [KPC-1f; decision 0470]: `true`
   * skips the pay-card step, anything else shows it. Never re-read later in the visit [0440's pattern].
   *
   * ⚠️ **Optional, and absent means the same as `null`.** AC-4 names three unknown states and two of them
   * are an absence: an answer without the key (the api half not deployed yet), and a lookup result the app
   * built itself (the unreachable fallback, a seeded session). Only `true` is ever read as on file, so an
   * absent key fails open to the step exactly as `null` does [0291; 0089].
   */
  readonly payCardOnFile?: PayCardOnFileState;
}

/** The throttle's answer: a rate, never a match status. */
export interface ThrottledResult {
  readonly retryAfterSeconds: number;
  /** True at the ceiling, where the recruiter hand-off appears IN ADDITION to the countdown. */
  readonly contactRecruiter: boolean;
}

export type LookupOutcome =
  | { readonly kind: 'diagnosed'; readonly result: LookupResult }
  | { readonly kind: 'throttled'; readonly result: ThrottledResult }
  /**
   * The api refused a factor's SHAPE — a `400` from its validation pipe (`email must be an email`,
   * `ssnLast4 must be exactly four digits`) [ISS-147].
   *
   * ⛔ **Never collapsed into `unreachable`.** Until ISS-147 it was, and `unreachable` lands on the neutral
   * hand-off, whose copy says a representative has been told — so a typo in an email produced a promise
   * of a person and no entry behind it [0220 L1]. A refusal names something the person standing there can
   * fix: the form keeps what they typed and says which field. `factors` is what the api named, narrowed
   * to the five this request carries; a refusal naming none of them yields an empty list, never a guess.
   */
  | { readonly kind: 'rejected'; readonly factors: readonly CheckInFactorName[] }
  /**
   * The request never produced an answer — offline, timeout, 5xx, unparseable body.
   *
   * Deliberately NOT collapsed into `assistance` here. The backend fails closed to `assistance` for its own
   * failures; this is the client's own transport failing, and keeping it distinct is what lets the UI offer
   * a retry instead of a dead end. Both end up on the same screen for the candidate.
   */
  | { readonly kind: 'unreachable' };

const LOOKUP_PATH = '/identity/lookup';

/**
 * Bounded, and short. A candidate is standing at the device: a request that hangs for thirty seconds is
 * indistinguishable from a broken kiosk, and the backend's own Bullhorn timeout is 8s, so anything past
 * roughly that is waiting for a response the backend has already given up on.
 */
const REQUEST_TIMEOUT_MS = 12_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/**
 * Parses what the api returned rather than trusting it (§ 6, "validate at the boundary").
 *
 * A body that does not carry a diagnosis we recognise is treated as unreachable, not coerced: rendering an
 * unknown diagnosis would mean guessing which flow a real person belongs in.
 */
function readLookupResult(body: unknown): LookupResult | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const candidate = body as Record<string, unknown>;

  const diagnosis = candidate.diagnosis;
  if (typeof diagnosis !== 'string') return undefined;
  if (!Object.values(DIAGNOSIS).includes(diagnosis as Diagnosis)) return undefined;

  return {
    diagnosis: diagnosis as Diagnosis,
    ...(typeof candidate.candidateId === 'number' ? { candidateId: candidate.candidateId } : {}),
    ...(typeof candidate.emailOnFile === 'string' ? { emailOnFile: candidate.emailOnFile } : {}),
    requiredCredentialsComplete: readRequiredCredentials(candidate.requiredCredentialsComplete),
    // Kept only when the api sent the key: a missing key stays missing, which is AC-4's own case (2).
    ...(Object.hasOwn(candidate, 'payCardOnFile')
      ? { payCardOnFile: readPayCardOnFile(candidate.payCardOnFile) }
      : {}),
  };
}

/**
 * The pay-card state, kept as the api sent it — and a malformed value is `null`, the unknown state
 * [KPC-1f AC-4]. ⛔ **Never a reason to call the lookup unreachable**: the tail shows the step on `null`, and
 * the candidate is otherwise exactly who the lookup said they are.
 */
function readPayCardOnFile(value: unknown): PayCardOnFileState {
  return typeof value === 'boolean' ? value : null;
}

/**
 * The credential state, kept as the api sent it — and an absent or malformed value is `null`, which is what
 * `null` already means: *"uninterpretable"* [0440]. ⛔ **Never a reason to call the lookup unreachable**: that
 * would send a real person to the hand-off over a field nothing reads.
 */
function readRequiredCredentials(value: unknown): RequiredCredentialsState {
  return typeof value === 'boolean' ? value : null;
}

/**
 * Raises a `400` that names no factor this request sends, where somebody can act on it [ISS-147].
 *
 * The candidate is left on the form with nothing marked, because nothing they typed was named — which means
 * the kiosk and the api disagree about the contract, and only a person reading the console can fix that.
 * Same shape and channel as `arrivalReason.ts`'s `reportRefusal` (`msg` · `alert` · `status`), for the
 * reason it gives. **Names only, never a value**: `refusedNames` is the leading word of each api message,
 * which is a property name, and nothing the candidate typed is read into it.
 */
function reportUnnamedRefusal(refusedNames: readonly string[]): void {
  console.error({
    msg: 'the api REFUSED an identity lookup and named no factor the kiosk sends — the kiosk and the api disagree about the contract',
    alert: 'identity_lookup_rejected_unnamed',
    status: 400,
    refusedNames,
  });
}

function readThrottledResult(body: unknown): ThrottledResult {
  const candidate = (typeof body === 'object' && body !== null ? body : {}) as Record<
    string,
    unknown
  >;
  return {
    // A missing or malformed duration falls back to the first backoff rung rather than to zero — zero would
    // render a countdown that is already finished and invite the candidate to retry straight into another 429.
    retryAfterSeconds:
      typeof candidate.retryAfterSeconds === 'number' && candidate.retryAfterSeconds > 0
        ? candidate.retryAfterSeconds
        : 180,
    contactRecruiter: candidate.contactRecruiter === true,
  };
}

/**
 * Runs the lookup.
 *
 * The four factors go in the **body**, never the query string: a `GET` would put a last-4 SSN and a date of
 * birth in the request line, where they land in access logs, proxy logs and browser history — none of which
 * this app can redact.
 *
 * This function never throws. Every failure is a value, because the caller's job is to put the candidate on
 * a working screen and an exception escaping into a render tree does the opposite.
 */
export async function lookupIdentity(factors: CheckInFactors): Promise<LookupOutcome> {
  /*
   * The body, and the one list of what a refusal may be read as naming: `satisfies` holds it to exactly the
   * five factors, so a property added here is a property a refusal can name, with no second list to update.
   */
  const payload = {
    email: factors.email.trim(),
    lastName: factors.lastName.trim(),
    ssnLast4: factors.ssnLast4,
    birthMonth: Number(factors.birthMonth),
    birthDay: Number(factors.birthDay),
  } satisfies Record<CheckInFactorName, unknown>;
  const isSent = (name: string): name is CheckInFactorName => Object.hasOwn(payload, name);

  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}${LOOKUP_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unreachable' };
  }

  const body: unknown = await response.json().catch(() => undefined);

  if (response.status === 429) {
    return { kind: 'throttled', result: readThrottledResult(body) };
  }

  if (response.status === 400) {
    const refusedNames = refusedFieldsIn(body);
    const refused = refusedNames.filter(isSent);
    if (refused.length === 0) reportUnnamedRefusal(refusedNames);
    return { kind: 'rejected', factors: refused };
  }

  if (!response.ok) return { kind: 'unreachable' };

  const result = readLookupResult(body);
  return result === undefined ? { kind: 'unreachable' } : { kind: 'diagnosed', result };
}
